import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HeroCarousel } from '@/components/HeroCarousel';
import { productsAPI } from '@/utils/api';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SITE_CATEGORIES } from '@/constants/categories';
import { ProductCarousel } from '@/components/ProductCarousel';
import { CategoryCarousel } from '@/components/CategoryCarousel';
import { AnimeFandomCarousel } from '@/components/AnimeFandomCarousel';

// 1. TEXTURE STYLE
// Applied to the section container so it shows behind the cards
const TEXTURE_STYLE = {
  background: 'linear-gradient(135deg, #fef3c7 0%, #ffffff 45%, #ffffff 55%, #fef3c7 100%)',
};

// 2. REUSABLE SECTION WRAPPER
const SectionWrapper = ({ title, tag, children, viewAllLink }) => (
  <section 
    className="py-12 border-b border-yellow-100/50" 
    style={TEXTURE_STYLE} 
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      
      {/* Header */}
      <div className="flex items-end justify-between mb-8">
        <div>
           {title && <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-outfit tracking-tight">{title}</h2>}
           {tag && <p className="text-gray-500 text-sm mt-1 font-medium">{tag}</p>}
        </div>
        {viewAllLink && (
           <Link to={viewAllLink} className="text-sm font-bold text-yellow-600 hover:text-yellow-700 flex items-center transition-colors">
             View All
           </Link>
        )}
      </div>

      {/* Carousel Content */}
      <div className="relative z-10 pl-2 md:pl-0">
        {children}
      </div>
      
    </div>
  </section>
);

export const HomePage = () => {
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [seasonalProducts, setSeasonalProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  
  // Specific Anime States
  const [jujutsuProducts, setJujutsuProducts] = useState([]);
  const [demonSlayerProducts, setDemonSlayerProducts] = useState([]);

  const [genderProducts, setGenderProducts] = useState({ men: [], women: [], unisex: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    const timeStamp = Date.now(); // Create a unique ID for this request

    try {
      const [
        all, 
        trending, 
        newArrivals, 
        seasonal, 
        men, 
        women, 
        unisex,
        jujutsu,
        demon
      ] = await Promise.all([
        productsAPI.getAll({ _t: timeStamp }),
        productsAPI.getAll({ trending: true, _t: timeStamp }),
        productsAPI.getAll({ is_new: true, _t: timeStamp }),
        productsAPI.getAll({ tag: 'seasonal', _t: timeStamp }), 
        productsAPI.getAll({ gender: 'men', _t: timeStamp }),
        productsAPI.getAll({ gender: 'women', _t: timeStamp }),
        productsAPI.getAll({ gender: 'unisex', _t: timeStamp }),
        productsAPI.getAll({ search: 'jujutsu', _t: timeStamp }), 
        productsAPI.getAll({ search: 'demon slayer', _t: timeStamp })
      ]);

      const slice = (arr) => Array.isArray(arr) ? arr.slice(0, 12) : [];

      setAllProducts(slice(all));
      setTrendingProducts(slice(trending));
      setNewProducts(slice(newArrivals));
      setSeasonalProducts(Array.isArray(seasonal) && seasonal.length > 0 ? slice(seasonal) : slice(all));
      
      setJujutsuProducts(slice(jujutsu));
      setDemonSlayerProducts(slice(demon));

      setGenderProducts({
        men: slice(men),
        women: slice(women),
        unisex: slice(unisex)
      });

    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fffbeb]">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-20 font-outfit" data-testid="home-page">
      
      <HeroCarousel />

      <SectionWrapper title="New Season Shirts" tag="Fresh looks for the new season" viewAllLink="/products?is_new=true">
        <ProductCarousel products={newProducts} />
      </SectionWrapper>

      <SectionWrapper title="Trending Now" tag="Street style picks" viewAllLink="/products?trending=true">
        <ProductCarousel products={trendingProducts} />
      </SectionWrapper>

      <SectionWrapper title="Men's Collection" viewAllLink="/products?gender=men">
        <ProductCarousel products={genderProducts.men} />
      </SectionWrapper>

      <SectionWrapper title="Women's Collection" viewAllLink="/products?gender=women">
        <ProductCarousel products={genderProducts.women} />
      </SectionWrapper>

      <SectionWrapper title="Unisex Collection" viewAllLink="/products?gender=unisex">
        <ProductCarousel products={genderProducts.unisex} />
      </SectionWrapper>

      <SectionWrapper title="New Season Shirts" tag="Seasonal Picks" viewAllLink="/products?tag=seasonal">
        <ProductCarousel products={seasonalProducts} />
      </SectionWrapper>

      <SectionWrapper title="Trending" viewAllLink="/products?trending=true">
        <ProductCarousel products={trendingProducts} />
      </SectionWrapper>

      {/* LATEST COLLECTIONS (With AutoScroll) */}
      <SectionWrapper title="Latest Collections" viewAllLink="/products">
         <CategoryCarousel
             genderId="men"
             autoPlay={true}
             subcategories={[
                 { name: "Graphic Printed", value: "graphic", image: "https://images.bewakoof.com/uploads/grid/app/Newtiles-6jan2026-graphic-men-1767769731.jpg" },
                 { name: "Plus Sized T-Shirts", value: "plus-sized", image: "https://images.bewakoof.com/uploads/grid/app/Newtiles-6jan2026-plus-men-1767769730.jpg" },
                 { name: "Full Sleeve", value: "full-sleeve", image: "https://images.bewakoof.com/uploads/grid/app/Newtiles-6jan2026-fs-men-1767769733.jpg" },
                 { name: "Striped Tees", value: "striped-tee", image: "https://images.bewakoof.com/uploads/grid/app/Newtiles-6jan2026-striped-men-1767769732.jpg" },
                 { name: "Classic Polos", value: "classic-polos", image: "https://images.bewakoof.com/uploads/grid/app/Newtiles-6jan2026-polo-men-1767769733.jpg" },
                 { name: "Everyday Track Pants", value: "everyday-track-pants", image: "https://images.bewakoof.com/uploads/grid/app/Newtiles-6jan2026-trackpants-men-1767769734.jpg" },
                 { name: "Washed Denims", value: "washed-denims", image: "https://images.bewakoof.com/uploads/grid/app/Newtiles-6jan2026-denim-men-1767769735.jpg" },
             ]}
        />
      </SectionWrapper>

      <SectionWrapper title="Buy 1 Get 1 Free" tag="Limited Time Offer" viewAllLink="/products">
        <ProductCarousel products={allProducts} />
      </SectionWrapper>

      <SectionWrapper title="Trend Inspired Collections" viewAllLink="/products?trending=true">
        <ProductCarousel products={trendingProducts} />
      </SectionWrapper>

      <div className="py-8" style={TEXTURE_STYLE}>
         <AnimeFandomCarousel />
      </div>

      {/* 13. JUJUTSU KAISEN */}
      <SectionWrapper title="Jujutsu Kaisen Domain" viewAllLink="/products?search=jujutsu">
        <ProductCarousel products={jujutsuProducts} />
      </SectionWrapper>

      {/* 14. DEMON SLAYER */}
      <SectionWrapper title="Demon Slayer Domain" viewAllLink="/products?search=demon">
        <ProductCarousel products={demonSlayerProducts} />
      </SectionWrapper>

      <SectionWrapper title="Too Hot to be missed!" tag="Selling Fast" viewAllLink="/products?tag=hot">
        <ProductCarousel products={trendingProducts} />
      </SectionWrapper>

    </div>
  );
};